import type {
  NcmDescriptionItem,
  NcmSearchLanguage,
  NcmSearchResponse,
} from "@backchannel/tax-contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useI18n } from "~/i18n";
import { useSession } from "~/lib/access/session-context";
import { useApi } from "./api-context";
import type { TaxApiClient } from "./client";

/*
 * Product → NCM search and NCM descriptions. Reference only: the official
 * Siscomex text is shown verbatim and translations say they are unofficial.
 * Readable with READ:TAX_CALCULATION:ALL, so every calculator user has it.
 */

export const NCM_SEARCH_PERMISSION = "READ:TAX_CALCULATION:ALL" as const;

export function ncmLanguage(locale: string): NcmSearchLanguage {
  return locale === "es" ? "es" : locale === "en" ? "en" : "pt";
}

export const ncmSearchKeys = {
  all: ["ncm-search"] as const,
  search: (lang: NcmSearchLanguage, q: string, limit: number) =>
    ["ncm-search", "search", lang, q, limit] as const,
  detail: (lang: NcmSearchLanguage, ncm: string) => ["ncm-search", "detail", lang, ncm] as const,
  description: (lang: NcmSearchLanguage, ncm: string) =>
    ["ncm-search", "description", lang, ncm] as const,
};

export function useCanSearchNcm(): boolean {
  const { can } = useSession();
  return can(NCM_SEARCH_PERMISSION);
}

/** Ranked suggestions for a product name or a code prefix. Pass the debounced text. */
export function useNcmSearch(q: string, options: { enabled?: boolean; limit?: number } = {}) {
  const api = useApi();
  const { locale } = useI18n();
  const lang = ncmLanguage(locale);
  const allowed = useCanSearchNcm();
  const needle = q.trim();
  const limit = options.limit ?? 8;
  return useQuery<NcmSearchResponse>({
    queryKey: ncmSearchKeys.search(lang, needle, limit),
    queryFn: ({ signal }) => api.searchNcm({ q: needle, lang, limit }, signal),
    enabled: allowed && needle.length >= 2 && (options.enabled ?? true),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useNcmDetail(ncm: string | undefined) {
  const api = useApi();
  const { locale } = useI18n();
  const lang = ncmLanguage(locale);
  const allowed = useCanSearchNcm();
  return useQuery({
    queryKey: ncmSearchKeys.detail(lang, ncm ?? ""),
    queryFn: ({ signal }) => api.getNcm(ncm ?? "", lang, signal),
    enabled: allowed && Boolean(ncm && /^\d{8}$/.test(ncm)),
    staleTime: 30 * 60_000,
    retry: false,
  });
}

/* -- Batched descriptions --------------------------------------------------- */

/** The client, or null where no ApiProvider is mounted (a description is optional). */
function useApiIfMounted(): TaxApiClient | null {
  try {
    return useApi();
  } catch {
    return null;
  }
}

/**
 * Tables show a description next to hundreds of codes. Each cell asks for its own code; the
 * loader gathers the codes asked for in the same tick and sends one request per 200 codes.
 */
const BATCH_WINDOW_MS = 12;
const BATCH_MAX = 200;

type Pending = {
  resolve: (value: NcmDescriptionItem | null) => void;
  reject: (error: unknown) => void;
};

class NcmDescriptionLoader {
  readonly #api: TaxApiClient;
  readonly #lang: NcmSearchLanguage;
  #queue = new Map<string, Pending[]>();
  #timer: ReturnType<typeof setTimeout> | undefined;

  constructor(api: TaxApiClient, lang: NcmSearchLanguage) {
    this.#api = api;
    this.#lang = lang;
  }

  load(ncm: string): Promise<NcmDescriptionItem | null> {
    return new Promise((resolve, reject) => {
      const waiting = this.#queue.get(ncm) ?? [];
      waiting.push({ resolve, reject });
      this.#queue.set(ncm, waiting);
      this.#timer ??= setTimeout(() => this.#flush(), BATCH_WINDOW_MS);
    });
  }

  #flush(): void {
    this.#timer = undefined;
    const queue = this.#queue;
    this.#queue = new Map();
    const codes = [...queue.keys()];
    for (let start = 0; start < codes.length; start += BATCH_MAX) {
      const chunk = codes.slice(start, start + BATCH_MAX);
      this.#api.describeNcms(chunk, this.#lang).then(
        (response) => {
          const found = new Map(response.items.map((item) => [item.ncm, item]));
          for (const code of chunk) {
            for (const waiting of queue.get(code) ?? []) waiting.resolve(found.get(code) ?? null);
          }
        },
        (error: unknown) => {
          for (const code of chunk) {
            for (const waiting of queue.get(code) ?? []) waiting.reject(error);
          }
        },
      );
    }
  }
}

const loaders = new WeakMap<TaxApiClient, Map<NcmSearchLanguage, NcmDescriptionLoader>>();

function loaderFor(api: TaxApiClient, lang: NcmSearchLanguage): NcmDescriptionLoader {
  let byLang = loaders.get(api);
  if (!byLang) {
    byLang = new Map();
    loaders.set(api, byLang);
  }
  let loader = byLang.get(lang);
  if (!loader) {
    loader = new NcmDescriptionLoader(api, lang);
    byLang.set(lang, loader);
  }
  return loader;
}

/** The official description (and a labelled translation) of one 8-digit code; null if unknown. */
export function useNcmDescription(ncm: string | null | undefined) {
  // Optional: a description is a convenience next to a code, never a reason for a screen to fail.
  const api = useApiIfMounted();
  const { locale } = useI18n();
  const lang = ncmLanguage(locale);
  const allowed = useCanSearchNcm();
  const code = ncm?.replace(/\D/g, "") ?? "";
  return useQuery({
    queryKey: ncmSearchKeys.description(lang, code),
    queryFn: () => (api ? loaderFor(api, lang).load(code) : Promise.resolve(null)),
    enabled: Boolean(api?.describeNcms) && allowed && /^\d{8}$/.test(code),
    staleTime: 60 * 60_000,
    gcTime: 60 * 60_000,
    retry: 1,
  });
}
